/**
 * What `/` and `/projects` resolve to once the guard has let the caller in.
 *
 * It exists so the "you have not set your company up yet" fork lives beside the session it is
 * decided from, rather than inside the home screen it is deciding *against* — a screen that
 * redirects away from itself is a screen whose own render is the wrong place to look for why.
 *
 * `Navigate` rather than a router `beforeLoad` redirect, for the same reason `RequireSession`
 * is a component: the session lives in the TanStack Query cache created above the router, and
 * a loader would need a second route to that cache (see `RequireSession`).
 *
 * **`/` IS AN ENTRANCE, NOT A SCREEN** (owner, 2026-09-17). Until that day `/` *was* B1, the
 * projects list, per chat1.md §1 ("the projects list is the workspace home"). The owner moved the
 * front door: the task page is what a person sees when they come into the app, and the projects
 * list moved to `/projects`. So `/` renders nothing of its own any more — it runs the forks below
 * and then redirects, and every link that meant "the list of projects" now says `/projects`.
 * This deliberately departs from chat1.md §1; the decision is recorded in `MCK-20260521-009` and
 * `KLQ-20260819-003`.
 *
 * TWO EXPORTS, ONE SET OF FORKS
 *
 *   - `HomeRoute` — `/`. Decides where the caller begins and sends them there.
 *   - `ProjectsHomeRoute` — `/projects`. The same protections around B1 for a person who typed
 *     the address or pressed the rail item, but **no landing redirect**: somebody who asked for
 *     the projects list is given it, whatever `landing` says.
 *
 * **B1 arrives as `children` rather than as an import.** It lives in `features/projects`, which
 * reads the session — importing it back here would close a cycle through two feature modules
 * for no gain. The router names the screen; this file decides whether the caller may see it.
 *
 * THE FORKS, in order, and the middle ones are the ones worth naming (KLQ-20260819-003):
 *
 *   - unclaimed home workspace, no projects → `/setup`, on both addresses. Somebody who came
 *     through the front door and has not said who they are yet.
 *   - on `/` only: `landing: "tasks"` → `/tasks`. The server's word (`auth.service.landing_for`):
 *     everybody who reaches at least one project starts on the work they owe.
 *   - unclaimed active workspace → the stub, still. An invited engineer from another firm has an
 *     unclaimed home workspace of their own, and MCK-20260812-009 §E says they are shown no
 *     workspace chrome at all — so they are not shown B1, which is chrome from its first pixel.
 *     On `/` this is only reached by somebody whose landing is `projects`; on `/projects` it is
 *     reached by anybody who typed the address.
 *   - claimed → `/projects` from `/`; B1 itself on `/projects`.
 *
 * Since `KLQ-20260819-007` the claimed fork is decided by the **active** workspace rather than by
 * home. See `useActiveWorkspaceIsClaimed`.
 */

import { Navigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { useActiveWorkspaceIsClaimed } from './activeWorkspace';
import { HomeStub } from './HomeStub';
import { needsWorkspaceSetup, useMe } from './queries';

/**
 * `/` — the entrance. Renders the stub or a redirect, never a screen of its own.
 */
export function HomeRoute() {
  const { data } = useMe();
  const claimed = useActiveWorkspaceIsClaimed();

  // `RequireSession` renders this only once `me` has resolved, so `data` is present; an
  // absent one falls through to the stub rather than to a redirect, because a redirect is the
  // answer that cannot be taken back.
  if (data === undefined) return <HomeStub />;

  if (needsWorkspaceSetup(data)) {
    return <Navigate to="/setup" replace />;
  }

  // The root task page (`MCK-20260521-009`). The server's word and nothing else: `landing` is
  // computed from authority in `auth.service.landing_for`, and a client reading role names
  // would send an admin to the wrong screen the day a role is added between two others.
  //
  // Since 2026-09-17 (owner) that is **everybody who reaches a project** — the task page is the
  // product's front door, for an admin as much as for a contractor. `projects` is left for the
  // person who reaches nothing, whom the projects list tells that they have not been added
  // anywhere.
  //
  // **Before the claimed fork, deliberately** (owner, 2026-09-13, phase-2 review item C). The
  // invited door mints every collaborator from a new domain an unclaimed home of their own, so
  // "active workspace unclaimed" and "lands on tasks" describe the *same* person — the external
  // engineer — and the stub's "set your company up" is not their question; their work is. The
  // setup fork above still wins: a founder with no company and no projects has no tasks to land
  // on, and `landing_for` answers `projects` for them anyway.
  if (data.landing === 'tasks') return <Navigate to="/tasks" replace />;

  // §E, the rendering half of MCK-20260812-009 :212. Drawn here rather than after a redirect to
  // `/projects`, so a person with no company of their own is not handed an address for a list
  // they are not shown. Keyed, since `KLQ-20260819-007`, off the workspace this tab is *acting
  // as* rather than off home: identical for anybody with one company; for somebody with two it
  // means an unclaimed home no longer hides the studio that hired them.
  if (!claimed) return <HomeStub />;

  return <Navigate to="/projects" replace />;
}

/**
 * `/projects` — B1 behind the same protections as the entrance, minus the landing redirect.
 */
export function ProjectsHomeRoute({ children }: { readonly children: ReactNode }) {
  const { data } = useMe();
  const claimed = useActiveWorkspaceIsClaimed();

  // As on `/`: no session payload yet is the stub, never a guess.
  if (data === undefined) return <HomeStub />;

  // A founder who has not set their company up has no list to be shown, whichever address
  // they typed.
  if (needsWorkspaceSetup(data)) {
    return <Navigate to="/setup" replace />;
  }

  // §E. An unclaimed active workspace is shown no workspace chrome, and B1 is chrome — so the
  // list is never drawn here. What replaces it depends on who asked (owner, 2026-09-17). The
  // external engineer — an unclaimed home minted by the invited door, and work on somebody
  // else's project — reaches this address from the rail and from every "back to projects"
  // link, and "set your company up" is not their question; their tasks are, exactly as on `/`.
  // Anybody else with an unclaimed active workspace still gets the stub. A claimed workspace is
  // never bounced: the person asked for the projects list and gets it, so the rail item goes
  // where it says.
  if (!claimed) {
    return data.landing === 'tasks' ? <Navigate to="/tasks" replace /> : <HomeStub />;
  }

  return <>{children}</>;
}
